import { STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT } from "@/constants";
import { ADDRESS_INITIAL_VALUES } from "@/constants/provider/patient/add-new-patient/add-new-patient-form";
import { PreferenceFormValues } from "@/components/app/provider/patient/add-new-patient/preferences/PatientLabPharmacyRadiologyFormDrawer";
import { PharmacyAddress, PharmacyEntry } from "./readmission-f2f-types";
import { newId } from "./readmission-f2f-utils";
import {
  NON_DIGIT_PATTERN,
  PHARMACY_PREFERENCE_TYPE,
} from "./readmission-f2f-constant";

const toRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null
    ? Object.fromEntries(Object.entries(value))
    : {};

const toText = (value: unknown): string =>
  typeof value === "string" ? value : "";

// The directory stores the state as its id ("43"); the name is shown instead.
const stateName = (state: string): string =>
  STATE_RECORD_FULL_NAME_MAPPER_WITHOUT_SHORT[state] ?? state;

const toAddressDetails = (address: unknown): PharmacyAddress => {
  const record = toRecord(address);
  return {
    line1: toText(record.line1),
    line2: toText(record.line2),
    city: toText(record.city),
    state: toText(record.state),
    country: toText(record.country),
    zipcode: toText(record.zipcode),
  };
};

const formatAddress = (address: unknown): string => {
  const record = toRecord(address);
  return ["line1", "line2", "city", "state", "zipcode", "country"]
    .map((key) => {
      const text = toText(record[key]).trim();
      return key === "state" ? stateName(text) : text;
    })
    .filter(Boolean)
    .join(", ");
};

/** The pharmacy directory hook is typed any; this reads the fields the section needs. */
export const toPharmacyEntries = (content: unknown): PharmacyEntry[] =>
  Array.isArray(content)
    ? content.map((item) => {
        const record = toRecord(item);
        return {
          id: toText(record.uuid) || newId("pharmacy"),
          uuid: toText(record.uuid) || undefined,
          name: toText(record.name),
          phone: toText(record.contactNumber),
          fax: toText(record.faxNumber),
          address: formatAddress(record.address),
          addressDetails: toAddressDetails(record.address),
        };
      })
    : [];

/** `record` is the saved directory entry from the server, when there is one. */
export const createPharmacyEntryFromPreference = (
  values: PreferenceFormValues,
  record?: unknown
): PharmacyEntry => ({
  id: newId("pharmacy"),
  uuid: toText(toRecord(record).uuid) || undefined,
  name: values.name.trim(),
  phone: values.contactNumber,
  fax: values.faxNumber,
  address: formatAddress(values.address),
  addressDetails: toAddressDetails(values.address),
});

/** Older entries only carry the formatted address, so it prefills line 1. */
export const toPharmacyFormValues = (
  entry: PharmacyEntry
): PreferenceFormValues => ({
  preferenceType: PHARMACY_PREFERENCE_TYPE,
  name: entry.name,
  contactNumber: entry.phone,
  faxNumber: entry.fax.replace(NON_DIGIT_PATTERN, ""),
  address: entry.addressDetails ?? {
    ...ADDRESS_INITIAL_VALUES,
    line1: entry.address,
  },
});

export const applyPharmacyEdit = (
  entry: PharmacyEntry,
  values: PreferenceFormValues
): PharmacyEntry => ({
  ...createPharmacyEntryFromPreference(values),
  id: entry.id,
  uuid: entry.uuid,
});
